import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useNavigateInApp } from '.';

import {
  QUERY_KEY,
  REDIRECT_BAD_STATUS_PAGE,
  RENTAL_FAILED_REASON,
  RENTAL_STATE,
  RentalFailedReason,
} from '~common';
import { SUCCESS_PAGE_PATH } from '~configs';
import { useLanguageContext } from '~providers';
import { getRequest, inMemoryJWTService } from '~services';
import {
  FailedRentalContext,
  NoRentableBatteryContext,
} from '~types/FailedRentalContext';
import { RentalStateResponse } from '~types/RentalStateResponse';
import { StationStatus } from '~types/station.type';

export const useQueryGetRentalState = (rentalId?: string) => {
  const navigate = useNavigateInApp();
  const [intervalDuration, setIntervalDuration] = useState(1000);

  const { lt } = useLanguageContext();

  const queryClient = useQueryClient();

  const isRecharging = (failedMetadata: NoRentableBatteryContext) => {
    return failedMetadata.lowChargeBatterySummary.count > 0;
  };

  const handleRedirectToBadStatusPage = useCallback(
    (
      failedReason?: RentalFailedReason,
      failedMetadata?: FailedRentalContext,
    ) => {
      let redirectPage: string | undefined = undefined;

      switch (failedReason) {
        case RentalFailedReason.NO_RENTABLE_BATTERY:
          if (
            failedMetadata &&
            failedMetadata.reason === RentalFailedReason.NO_RENTABLE_BATTERY &&
            isRecharging(failedMetadata)
          ) {
            redirectPage = REDIRECT_BAD_STATUS_PAGE[StationStatus.RECHARGING];
          } else {
            redirectPage = REDIRECT_BAD_STATUS_PAGE[failedReason];
          }
          break;
        case RentalFailedReason.STATION_OFFLINE:
          redirectPage = REDIRECT_BAD_STATUS_PAGE[failedReason];
          break;
        case RentalFailedReason.STATION_CAN_NOT_EJECT_BATTERY:
        case RentalFailedReason.FAILED_TO_INVOKE_IOT:
        case RentalFailedReason.TIMED_OUT:
          redirectPage = REDIRECT_BAD_STATUS_PAGE[failedReason];
      }

      if (redirectPage) {
        navigate(redirectPage);
      } else {
        const message = lt(
          'text.hooks.useQueryGetRentalState.error',
          'An error occurred when requesting the battery.',
        );
        toast.error(
          failedReason
            ? lt(
                `text.hooks.useQueryGetRentalState.RENTAL_FAILED_REASON.${failedReason}`,
                RENTAL_FAILED_REASON[failedReason],
              ) || message
            : message,
        );
      }
    },
    [lt, navigate],
  );

  const queryInfo = useQuery<RentalStateResponse>(
    [QUERY_KEY.GET_RENTAL_STATE, rentalId],
    () => {
      return getRequest({
        path: `api/rental/rental-state?id=${rentalId}`,
      }).then((res) => res);
    },
    {
      onSuccess: (data) => {
        if (data.rentalState !== RENTAL_STATE.WAITING) {
          switch (data.rentalState) {
            case RENTAL_STATE.FAILED:
              handleRedirectToBadStatusPage(
                data?.failedReason,
                data?.failedMetadata,
              );
              break;
            case RENTAL_STATE.ACTIVE:
              // The station has confirmed the ejection, so the swap count and
              // a first period's end time have just changed. Refresh both
              // subscription queries here — doing it back when the rental was
              // requested only fetched the old numbers.
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_USER_SUBSCRIPTION_BY_PHONE_NUMBER],
              });
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_CURRENT_SUBSCRIPTION],
              });
              navigate(SUCCESS_PAGE_PATH, { replace: true });
              break;
          }
        }
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          toast.error(
            error?.response?.data?.message ??
              lt(
                'text.hooks.useQueryGetRentalState.error',
                'An error occurred when requesting the battery.',
              ),
          );
        } else {
          toast.error(
            lt(
              'text.hooks.useQueryGetRentalState.error',
              'An error occurred when requesting the battery.',
            ),
          );
        }
      },

      enabled: !!rentalId && !!inMemoryJWTService.getCurrentToken(),
    },
  );

  useEffect(() => {
    // Set an interval to periodically invalidate the query with increasing duration.
    const interval = setInterval(() => {
      // Check the condition and invalidate the query if needed.
      if (queryInfo.data?.rentalState === RENTAL_STATE.WAITING) {
        queryClient.invalidateQueries([QUERY_KEY.GET_RENTAL_STATE, rentalId]);
        setIntervalDuration(intervalDuration * 2); // Double the interval duration
      }
    }, intervalDuration);

    // Clear the interval when the component unmounts to avoid memory leaks.
    return () => clearInterval(interval);
  }, [queryClient, rentalId, queryInfo, intervalDuration]);

  return queryInfo;
};
